import { ERROR_CATALOG, type ErrorCode } from "@typedoc/contracts/errors";
import { SAMPLE_APPROVAL_ID } from "./sample-approvals";
import type { Chat, ChatFile, ChatMessage, ChatProcess, ChatStep, Starter } from "./types";

/**
 * Sample chats for a firm called Marlow & Co. (Paper CH1 to CH2). Every time is counted back from
 * `now`, so the list always reads "Today", "Yesterday" and "Last 7 days" whenever it is opened,
 * and the weekday labels follow. Nothing here is real: names, suppliers and amounts are invented.
 *
 * The first nine chats and the three after them are the ones the Paper boards draw, with the
 * words they use, so the screens can be compared board against browser. The searches the boards
 * show ("brightline": 7 results in 4 chats) hold against this data; `view.test.ts` checks it. Two
 * more, early in October, are not on the boards: they end in a run that stopped, so the notice
 * that says why can be looked at and tested here (`STOPPED_CHAT_IDS`). One more, in August, is not
 * on the boards either: `prepayment-release` carries a plan, so the plan card can be seen finished
 * and left part done (`invoice-delays` shows one live, while its last answer waits on an approval).
 */

const lines = (rows: readonly string[]) => rows.join("\n");

/** A moment `daysAgo` calendar days before `now`, at a clock time. */
function at(now: Date, daysAgo: number, hour: number, minute = 0): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysAgo, hour, minute);
}

/** A run that ended without an answer, as the screens read it: the catalog's code and words. */
const stoppedFor = (code: ErrorCode): NonNullable<Chat["stopped"]> => ({
  code,
  message: ERROR_CATALOG[code].message,
});

/** An ask and Abacor's answer to it: all most chats in the sample hold. */
type Quick = {
  id: string;
  title: string;
  daysAgo: number;
  hour: number;
  minute?: number;
  by?: Starter;
  ask: string;
  reply: string;
  needsYou?: boolean;
  process?: ChatProcess;
  files?: readonly Pick<ChatFile, "name">[];
  /** The run ended before it finished the answer, for this reason. */
  stopped?: ErrorCode;
};

function quick(now: Date, chat: Quick): Chat {
  const lastAt = at(now, chat.daysAgo, chat.hour, chat.minute ?? 0);
  const startedAt = new Date(lastAt.getTime() - 6 * 60_000);
  const by = chat.by ?? "me";
  return {
    id: chat.id,
    title: chat.title,
    startedBy: by,
    startedAt,
    lastAt,
    preview: chat.reply,
    needsYou: chat.needsYou ?? false,
    ...(chat.process ? { process: chat.process } : {}),
    ...(chat.stopped ? { stopped: stoppedFor(chat.stopped) } : {}),
    files: (chat.files ?? []).map((file, index) => ({
      id: `${chat.id}-f${String(index + 1)}`,
      name: file.name,
      at: startedAt,
    })),
    steps: [],
    messages: [
      { id: `${chat.id}-m1`, author: by, at: startedAt, text: chat.ask },
      {
        id: `${chat.id}-m2`,
        author: "assistant",
        at: lastAt,
        text: chat.reply,
        ...(chat.needsYou ? { asked: true } : {}),
      },
    ],
  };
}

/** A chat from before this month, as one line of sample data: ids, when, who, ask and answer. */
type OlderRow = readonly [
  id: string,
  title: string,
  daysAgo: number,
  hour: number,
  by: Starter,
  ask: string,
  reply: string,
];

/** The two sample chats whose latest run stopped, by the reason it did. */
export const STOPPED_CHAT_IDS = {
  runLimit: "year-end-ledger-review",
  tooLong: "quarter-close-notes",
} as const;

export function sampleChats(now: Date): Chat[] {
  const day = (daysAgo: number, hour: number, minute = 0) => at(now, daysAgo, hour, minute);

  const invoiceMessages: ChatMessage[] = [
    {
      id: "invoice-delays-m1",
      author: "me",
      at: day(3, 9, 40),
      text: "Which supplier bills are stuck in Xero, and why?",
    },
    {
      id: "invoice-delays-m2",
      author: "assistant",
      at: day(3, 9, 42),
      text: "Four bills are waiting. Three share one blocker: no purchase order on file. The fourth, from Harbour Legal, is held because its bank details changed.",
      steps: [
        {
          id: "invoice-delays-t1",
          app: "Xero",
          text: "Looked at 4 bills in Draft",
          status: "done",
          access: "look",
          at: day(3, 9, 41),
        },
        {
          id: "invoice-delays-t2",
          app: "Gmail",
          text: "Searched Gmail for supplier replies",
          status: "done",
          access: "look",
          at: day(3, 9, 41),
        },
      ],
    },
    {
      id: "invoice-delays-b1",
      author: "me",
      at: day(0, 9, 20),
      text: "Harbour Legal changed their bank details last month and nobody noticed. Can you check them on every bill?",
    },
    {
      id: "invoice-delays-b2",
      author: "assistant",
      at: day(0, 9, 24),
      text: "I've drafted a new step in r4, after 02: the bank account on the invoice must match the one in Xero for this supplier. If it doesn't, I stop and tell you rather than ask, since a changed account can be fraud. It's open beside this chat. Nothing changes until you add it.",
      artifact: { processId: "prepare-supplier-bills", focus: "proposal:proposal-bank" },
    },
    {
      id: "invoice-delays-b3",
      author: "me",
      at: day(0, 9, 30),
      text: "Also make sure every line has a tracking category.",
    },
    {
      id: "invoice-delays-b4",
      author: "assistant",
      at: day(0, 9, 33),
      text: "I've proposed that on step 04. It changes when a line counts as done, so it waits for you.",
      artifact: { processId: "prepare-supplier-bills", focus: "s04" },
    },
    {
      id: "invoice-delays-m3",
      author: "me",
      at: day(0, 9, 38),
      text: "Which of the three has waited longest?",
    },
    {
      // A turn that only wrote a note, as the real agent leaves one: its words come in the next turn.
      id: "invoice-delays-m3a",
      author: "assistant",
      at: day(0, 9, 39),
      text: "",
      notes: [
        {
          id: "invoice-delays-n0",
          text: "Looking at the supplier bills that have no purchase order.",
          at: day(0, 9, 39),
        },
      ],
    },
    {
      id: "invoice-delays-m4",
      author: "assistant",
      at: day(0, 9, 41),
      text: 'Brightline Print has waited longest, 6 days, with no purchase order on file :cite[s1]. The policy holds a bill like that :cite[s2]{quote="A bill with no purchase order number is held until one is quoted."}. I put the stuck bills in a sheet, longest wait first. I can email their accounts team to ask for the number. Do you want me to send it?',
      // A file Abacor made, shown under its words and listed with the chat's files.
      files: [
        {
          id: "invoice-delays-made-1",
          name: "Stuck bills.xlsx",
          mediaType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          size: 18_400,
        },
      ],
      // The file it read and the code it ran in its sandbox to make the sheet.
      work: [
        {
          id: "invoice-delays-w1",
          at: day(0, 9, 39),
          kind: "read",
          status: "done",
          fileId: "invoice-delays-f2",
        },
        {
          id: "invoice-delays-w2",
          at: day(0, 9, 40),
          kind: "run",
          status: "done",
          purpose: "find bills with no PO number",
          code: 'rows = [r for r in bills if not r["po"]]\nrows.sort(key=lambda r: r["waited"], reverse=True)\nprint(len(rows), "of", len(bills))',
          printed: "3 of 4",
          exitCode: 0,
          ms: 2400,
        },
        {
          id: "invoice-delays-w3",
          at: day(0, 9, 40),
          kind: "run",
          status: "done",
          purpose: "write the sheet, longest wait first",
          code: 'wb.save("output/Stuck bills.xlsx")',
          printed: "",
          exitCode: 0,
          ms: 1900,
          made: [
            {
              id: "invoice-delays-made-1",
              name: "Stuck bills.xlsx",
              mediaType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              size: 18_400,
            },
          ],
        },
      ],
      // What it read to say so: the bills in Xero, and page 3 of the policy in the chat's files.
      sources: [
        { key: "s1", kind: "step", at: day(0, 9, 40), stepId: "invoice-delays-t3" },
        { key: "s2", kind: "file", at: day(0, 9, 40), fileId: "invoice-delays-f2", page: 3 },
      ],
      // The checklist it wrote for this answer. It waits on the approval below, so the card is live:
      // the first three are done and the one that needs the person's yes is the one in progress.
      plan: {
        at: day(0, 9, 41),
        items: [
          { text: "Find the bills with no purchase order number", status: "done" },
          { text: "Work out how long each one has waited", status: "done" },
          { text: "Put them in a sheet, longest wait first", status: "done" },
          { text: "Ask the supplier's accounts team for the number", status: "active" },
          { text: "Tell you what comes back", status: "pending" },
        ],
      },
      notes: [
        {
          id: "invoice-delays-n1",
          text: "Comparing the three bills with no purchase order by how many days each has waited.",
          at: day(0, 9, 39),
        },
      ],
      steps: [
        {
          id: "invoice-delays-t3",
          app: "Xero",
          text: "Looked at how long each bill has waited",
          status: "done",
          access: "look",
          at: day(0, 9, 40),
        },
        {
          id: "invoice-delays-t4",
          app: "Gmail",
          text: "Send a reminder to Brightline Print",
          status: "waiting",
          access: "send",
          at: day(0, 9, 41),
        },
      ],
      request: {
        id: "invoice-delays-r1",
        kind: "approval",
        prompt: 'Gmail ("Accounts inbox") wants to send email (Send messages).',
        status: "pending",
        canAnswer: true,
        approvalId: SAMPLE_APPROVAL_ID,
        response: {
          type: "choice",
          options: [
            { id: "approve", label: "This once" },
            { id: "deny", label: "Deny" },
          ],
        },
      },
    },
  ];
  const invoiceSteps: ChatStep[] = [
    {
      id: "invoice-delays-s1",
      at: day(3, 9, 41),
      app: "Xero",
      text: "Read 4 bills in Draft · Xero",
    },
    {
      id: "invoice-delays-s2",
      at: day(3, 9, 41),
      app: "Gmail",
      text: "Searched Gmail for supplier replies · 11 replies",
    },
  ];
  const invoiceFiles: ChatFile[] = [
    {
      id: "invoice-delays-f1",
      name: "Brightline contract.pdf",
      at: day(3, 9, 40),
      pages: [
        {
          page: 2,
          text: "Brightline Print will quote a purchase order number on every invoice. Invoices without one may be returned.",
        },
      ],
    },
    {
      id: "invoice-delays-f2",
      name: "Supplier bill policy.pdf",
      at: day(3, 9, 40),
      pages: [
        { page: 3, text: "A bill with no purchase order number is held until one is quoted." },
      ],
    },
    {
      id: "invoice-delays-made-1",
      name: "Stuck bills.xlsx",
      made: true,
      at: day(0, 9, 41),
      mediaType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      size: 18_400,
    },
  ];

  const monthlyClose: Chat = {
    id: "monthly-close",
    title: "Monthly close",
    startedBy: "me",
    startedAt: day(10, 10, 15),
    lastAt: day(5, 16, 32),
    preview: "Accruals are posted. The bank reconciliation still has 2 open items.",
    needsYou: false,
    process: { state: "draft" },
    files: [],
    steps: [
      {
        id: "monthly-close-s1",
        at: day(5, 16, 31),
        app: "Xero",
        text: "Read the bank lines in Xero",
      },
    ],
    messages: [
      {
        id: "monthly-close-m1",
        author: "me",
        at: day(10, 10, 15),
        text: "Start the October close. What do you need from me?",
      },
      {
        id: "monthly-close-m2",
        author: "assistant",
        at: day(10, 10, 18),
        text: "I need the payroll journal approved and the bank feeds caught up. I'll check the accruals once those are in.",
      },
      {
        id: "monthly-close-m3",
        author: "me",
        at: day(6, 14, 5),
        text: "Are the accruals done?",
      },
      {
        id: "monthly-close-m4",
        author: "assistant",
        at: day(6, 14, 7),
        text: "Rent and September's utilities are accrued. The October utilities invoice has not arrived yet.",
      },
      {
        id: "monthly-close-m5",
        author: "me",
        at: day(5, 16, 30),
        text: "What is still open on the bank reconciliation?",
      },
      {
        id: "monthly-close-m6",
        author: "assistant",
        at: day(5, 16, 32),
        text: "Two items were still open when we stopped.",
        table: {
          title: "Open bank items",
          columns: [
            { key: "date", label: "Bank date" },
            { key: "line", label: "Bank line" },
            { key: "amount", label: "Amount", align: "end" },
            { key: "status", label: "Status" },
          ],
          rows: [
            {
              date: "Oct 12",
              line: "Kestrel Cloud · card payment",
              amount: "1,284.00",
              status: "Needs a match",
            },
            { date: "Oct 13", line: "Stripe payout", amount: "3,910.55", status: "Needs a match" },
          ],
        },
      },
    ],
  };

  // The one sample chat whose answers use the answer widgets: a process link, a table, a nested
  // card. Its list line and time stay as the Paper board draws them; the exchange after the first
  // is a longer answer in the drawn form, and a reply streams another (`SAMPLE_ANSWERS`).
  const accrualBase = quick(now, {
    id: "accrual-questions",
    title: "Accrual questions",
    daysAgo: 4,
    hour: 13,
    minute: 45,
    ask: "Is rent accrued for October?",
    reply: "Rent is accrued. I still need the October utilities invoice.",
    files: [{ name: "Lease schedule.pdf" }],
  });
  const accrualQuestions: Chat = {
    ...accrualBase,
    messages: [
      ...accrualBase.messages,
      {
        id: "accrual-questions-m3",
        author: "me",
        at: day(4, 13, 50),
        text: "What happens to the October utilities invoice when it arrives?",
      },
      {
        id: "accrual-questions-m4",
        author: "assistant",
        at: day(4, 13, 52),
        text: lines([
          "It goes through :process[Prepare supplier bills]{id=prepare-supplier-bills} like any other bill. Until then I accrue it against October, so the books do not wait for it.",
          "",
          "| Item | Accrued | Status |",
          "|---|---:|---|",
          "| Rent | $4,200.00 | :tag[Posted]{tone=success} |",
          "| Utilities | $612.40 | :tag[Estimate]{tone=warning} |",
          "| Lease | | :file[Lease schedule.pdf] |",
          "",
          '::::card{title="Utilities, October" tag="Waiting" tone="warning"}',
          "The invoice has not arrived. Until it does I use last month's amount.",
          "",
          ':::card{title="If it differs by more than 10%" tag="Asks you"}',
          "I stop and ask :person[Tom] before I post the difference.",
          ":::",
          "::::",
        ]),
      },
    ],
  };

  const recent: Chat[] = [
    {
      id: "invoice-delays",
      title: "Invoice delays",
      startedBy: "me",
      startedAt: day(3, 9, 40),
      lastAt: day(0, 9, 41),
      preview: "Four bills are waiting. Three share one blocker: no purchase order on file.",
      needsYou: false,
      // The one sample chat with a request to answer, so the approval card can be tried here.
      canAct: true,
      process: { name: "Prepare supplier bills", state: "draft", id: "prepare-supplier-bills" },
      files: invoiceFiles,
      steps: invoiceSteps,
      messages: invoiceMessages,
    },
    {
      id: "xero-coding-rules",
      title: "Xero coding rules",
      startedBy: "me",
      startedAt: day(0, 8, 2),
      lastAt: day(0, 8, 15),
      preview: "I'd code Northstar's software to 6420. Can you confirm?",
      needsYou: true,
      canAct: true,
      files: [],
      steps: [],
      messages: [
        {
          id: "xero-coding-rules-m1",
          author: "me",
          at: day(0, 8, 2),
          text: "Code the Northstar bills the way we did last quarter, and tell me if anything differs.",
        },
        {
          id: "xero-coding-rules-m2",
          author: "assistant",
          at: day(0, 8, 14),
          text: "Brightline Print is coded to 6410 on every bill so far. I'd keep that. Can you confirm?",
          asked: true,
        },
        {
          id: "xero-coding-rules-m3",
          author: "assistant",
          at: day(0, 8, 15),
          text: "I'd code Northstar's software to 6420. Can you confirm?",
          asked: true,
          // The one sample question put as options, so the card for it can be tried here.
          request: {
            id: "xero-coding-rules-r1",
            kind: "question",
            prompt: "I'd code Northstar's software to 6420. Can you confirm?",
            status: "pending",
            canAnswer: true,
            response: {
              type: "choice",
              options: [
                { id: "0", label: "Yes, code it to 6420" },
                { id: "1", label: "No, keep it with the other software costs" },
                { id: "2", label: "Leave it uncoded for now" },
              ],
            },
          },
        },
      ],
    },
    quick(now, {
      id: "bank-change-checks",
      title: "Bank change checks",
      daysAgo: 1,
      hour: 16,
      minute: 40,
      by: "tom",
      ask: "Harbour Legal emailed new account details for their next invoice. Is that safe to pay?",
      reply:
        "Harbour Legal **changed account details**. Held for a phone check before `INV-2041` is paid.",
      needsYou: true,
    }),
    {
      id: "brightline-po-follow-up",
      title: "Brightline PO follow-up",
      startedBy: "me",
      startedAt: day(1, 10, 48),
      lastAt: day(1, 11, 2),
      preview: "Reminder sent. I'll look again on Friday.",
      needsYou: false,
      process: { name: "Chase missing POs", state: "live" },
      files: [],
      steps: [
        {
          id: "brightline-po-follow-up-s1",
          at: day(1, 10, 51),
          app: "Gmail",
          text: "Searched Gmail for “brightline” · 14 replies",
        },
      ],
      messages: [
        {
          id: "brightline-po-follow-up-m1",
          author: "me",
          at: day(1, 10, 48),
          text: "Can you chase Brightline for the PO on INV-2291? It has been six days.",
        },
        {
          id: "brightline-po-follow-up-m2",
          author: "assistant",
          at: day(1, 11, 2),
          text: "I sent Brightline a reminder asking for the purchase order. I'll look again on Friday.",
        },
      ],
    },
    quick(now, {
      id: "onboarding-questions",
      title: "Onboarding questions",
      daysAgo: 3,
      hour: 15,
      minute: 20,
      ask: "What should I ask a new client before we take on their books?",
      reply: "Here is the checklist I'd send to new clients.",
    }),
    quick(now, {
      id: "payroll-cutoff-dates",
      title: "Payroll cutoff dates",
      daysAgo: 3,
      hour: 11,
      minute: 10,
      ask: "When is the payroll cutoff for the part-time staff?",
      reply: "Which cutoff applies to the two part-time staff, the 20th or month end?",
      needsYou: true,
    }),
    accrualQuestions,
    monthlyClose,
    quick(now, {
      id: "year-end-document-requests",
      title: "Year-end document requests",
      daysAgo: 5,
      hour: 10,
      minute: 25,
      by: "tom",
      ask: "Which clients are still missing documents for year end?",
      reply: "I drafted the reminder for the three clients still missing documents.",
      process: { state: "draft" },
    }),
  ];

  const earlierThisMonth: Chat[] = [
    // Abacor ran out of room partway through its answer (a run is capped in time and cost).
    quick(now, {
      id: STOPPED_CHAT_IDS.runLimit,
      title: "Year-end ledger review",
      daysAgo: 8,
      hour: 10,
      ask: "Go through every ledger account for the year and flag anything unusual.",
      reply:
        "I have checked 31 of the 142 accounts so far. Nothing looks wrong yet, and 6460 is the first with unusual entries.",
      stopped: "RUN_LIMIT_REACHED",
    }),
    // The conversation grew past what Abacor can read, so the last message got no answer at all.
    {
      id: STOPPED_CHAT_IDS.tooLong,
      title: "Quarter close notes",
      startedBy: "me",
      startedAt: day(9, 14, 0),
      lastAt: day(9, 16, 20),
      preview:
        "Here are the Q3 close notes: accruals posted, payroll journal approved, two bank items open.",
      needsYou: false,
      stopped: stoppedFor("CHAT_TOO_LONG"),
      files: [],
      steps: [],
      messages: [
        {
          id: `${STOPPED_CHAT_IDS.tooLong}-m1`,
          author: "me",
          at: day(9, 14, 0),
          text: "Pull together the Q3 close notes from everything we covered.",
        },
        {
          id: `${STOPPED_CHAT_IDS.tooLong}-m2`,
          author: "assistant",
          at: day(9, 14, 6),
          text: "Here are the Q3 close notes: accruals posted, payroll journal approved, two bank items open.",
        },
        {
          id: `${STOPPED_CHAT_IDS.tooLong}-m3`,
          author: "me",
          at: day(9, 16, 20),
          text: "Add the foreign currency revaluation to the notes as well.",
        },
      ],
    },
    quick(now, {
      id: "q3-gst-review",
      title: "Q3 GST review",
      daysAgo: 10,
      hour: 14,
      by: "tom",
      ask: "Does the Q3 GST return agree with the ledger?",
      reply: "The return agrees to the ledger within $3.20 of rounding.",
    }),
    {
      id: "supplier-statements",
      title: "Supplier statements",
      startedBy: "me",
      startedAt: day(12, 9, 30),
      lastAt: day(12, 9, 33),
      preview: "Only invoices. I found no statement emails from that supplier in a year.",
      needsYou: false,
      files: [],
      steps: [],
      messages: [
        {
          id: "supplier-statements-m1",
          author: "me",
          at: day(12, 9, 30),
          text: "Does Brightline send monthly statements, or only invoices?",
        },
        {
          id: "supplier-statements-m2",
          author: "assistant",
          at: day(12, 9, 33),
          text: "Only invoices. I found no statement emails from that supplier in the last 12 months.",
        },
      ],
    },
    quick(now, {
      id: "petty-cash-policy",
      title: "Petty cash policy",
      daysAgo: 14,
      hour: 16,
      ask: "Draft a one-page petty cash policy for the office.",
      reply: "Here is a one-page policy. The float is $200 and receipts are due weekly.",
    }),
  ];

  const olderRows: readonly OlderRow[] = [
    [
      "august-close-checklist",
      "August close checklist",
      23,
      10,
      "me",
      "Where did the August close stall?",
      "It stalled on the bank feed. The feed was reconnected on the 4th.",
    ],
    [
      "contractor-payments",
      "Contractor payments",
      24,
      15,
      "tom",
      "Who are we paying as a contractor this quarter?",
      "Six contractors. Two have no tax form on file.",
    ],
    [
      "bank-feed-outage",
      "Bank feed outage",
      26,
      9,
      "me",
      "The Westpac feed stopped. Why?",
      "The bank asked for a fresh consent. It is renewed and lines are catching up.",
    ],
    [
      "fixed-asset-register",
      "Fixed asset register",
      27,
      14,
      "me",
      "Add the new laptops to the asset register.",
      "Three laptops added, depreciating over three years.",
    ],
    [
      "client-onboarding-harbour-legal",
      "Client onboarding: Harbour Legal",
      28,
      11,
      "tom",
      "Set up Harbour Legal as a new client.",
      "Set up. I still need their engagement letter.",
    ],
    [
      "sales-tax-filing",
      "Sales tax filing",
      30,
      16,
      "me",
      "Is the September filing ready to lodge?",
      "Ready. The payable is $18,240 and it matches the ledger.",
    ],
    [
      "payroll-journal",
      "Payroll journal",
      31,
      10,
      "me",
      "Post the payroll journal for September.",
      "Posted. Super is $4,120 and is due on the 28th.",
    ],
    [
      "expense-claims-policy",
      "Expense claims policy",
      32,
      13,
      "tom",
      "What does our policy say about meals?",
      "Meals are claimable up to $60 a head with a receipt.",
    ],
    [
      "prepaid-insurance",
      "Prepaid insurance",
      34,
      9,
      "me",
      "How should the insurance premium be spread?",
      "Over twelve months. I set up the monthly journal.",
    ],
    [
      "foreign-currency-revaluation",
      "Foreign currency revaluation",
      35,
      15,
      "me",
      "Revalue the USD balances at month end.",
      "Revalued. The unrealized gain is $1,180.",
    ],
    [
      "audit-requests-orbit-office",
      "Audit requests: Orbit Office",
      38,
      11,
      "tom",
      "List what the auditors asked Orbit Office for.",
      "Nine items. Five are done and four are with the client.",
    ],
    [
      "late-fee-waiver",
      "Late fee waiver",
      40,
      14,
      "me",
      "Draft a request to waive the late fee.",
      "Drafted. It cites the bank feed failure on the 12th.",
    ],
    [
      "supplier-onboarding-form",
      "Supplier onboarding form",
      41,
      10,
      "me",
      "Make a form for new suppliers to fill in.",
      "Here is a one-page form with bank details and a tax number.",
    ],
    [
      "intercompany-loans",
      "Intercompany loans",
      43,
      16,
      "tom",
      "Does the intercompany loan balance agree?",
      "It agrees. The difference is $0.00 on both sides.",
    ],
    [
      "depreciation-schedule",
      "Depreciation schedule",
      44,
      9,
      "me",
      "Refresh the depreciation schedule for the year.",
      "Refreshed. Depreciation for the year is $9,640.",
    ],
    [
      "chart-of-accounts-cleanup",
      "Chart of accounts cleanup",
      47,
      13,
      "me",
      "Which accounts have not been used this year?",
      "Fourteen accounts. I'd archive nine and keep five.",
    ],
    [
      "retainer-billing",
      "Retainer billing",
      52,
      11,
      "tom",
      "Which retainers did we not bill in August?",
      "Two. Both are drafted for your approval.",
    ],
    [
      "tax-residency-question",
      "Tax residency question",
      56,
      15,
      "me",
      "Does a client's move overseas change their residency?",
      "Possibly. It depends on where their home and work are. I listed what to ask.",
    ],
    [
      "month-end-checklist-draft",
      "Month-end checklist draft",
      60,
      10,
      "me",
      "Draft a month-end checklist from how we close.",
      "Drafted twelve steps. Three need your wording.",
    ],
    [
      "receipt-matching",
      "Receipt matching",
      63,
      14,
      "tom",
      "Match last month's receipts to the card feed.",
      "Matched 41 of 46. Five receipts are missing.",
    ],
    [
      "bad-debt-provision",
      "Bad debt provision",
      68,
      9,
      "me",
      "Should we provide for the Orchard Freight balance?",
      "I'd provide 50%. The balance is 90 days overdue.",
    ],
    [
      "xero-tracking-categories",
      "Xero tracking categories",
      72,
      16,
      "me",
      "Set up tracking categories by service line.",
      "Set up with three service lines. Existing bills are untagged.",
    ],
  ];
  const older = olderRows.map(([id, title, daysAgo, hour, by, ask, reply]) =>
    quick(now, { id, title, daysAgo, hour, by, ask, reply }),
  );

  // Two turns with a plan: the first answer finished it, and the second ran out with part of it
  // undone and no words, so the plan card can be seen folded, opened and as it was left.
  const prepayments: Chat = {
    id: "prepayment-release",
    title: "Prepayment release",
    startedBy: "me",
    startedAt: day(70, 10, 5),
    lastAt: day(70, 10, 31),
    preview: "The schedule agrees to the ledger. Two prepayments end this month.",
    needsYou: false,
    files: [],
    steps: [],
    messages: [
      {
        id: "prepayment-release-m1",
        author: "me",
        at: day(70, 10, 5),
        text: "Does the prepayment schedule agree to the ledger?",
      },
      {
        id: "prepayment-release-m2",
        author: "assistant",
        at: day(70, 10, 12),
        text: "The schedule agrees to the ledger. Two prepayments end this month.",
        steps: [
          {
            id: "prepayment-release-t1",
            app: "Xero",
            text: "Looked at the prepayments account",
            status: "done",
            access: "look",
            at: day(70, 10, 9),
          },
        ],
        plan: {
          at: day(70, 10, 11),
          items: [
            { text: "Read the schedule and the ledger balance", status: "done" },
            { text: "Compare each prepayment line by line", status: "done" },
            { text: "List the ones that end this month", status: "done" },
          ],
        },
      },
      {
        id: "prepayment-release-m3",
        author: "me",
        at: day(70, 10, 25),
        text: "Now draft the release journals for those two.",
      },
      {
        // A turn that ended without words, with the plan left part done.
        id: "prepayment-release-m4",
        author: "assistant",
        at: day(70, 10, 31),
        text: "",
        steps: [
          {
            id: "prepayment-release-t2",
            app: "Xero",
            text: "Looked at the two prepayments that end this month",
            status: "done",
            access: "look",
            at: day(70, 10, 28),
          },
        ],
        plan: {
          at: day(70, 10, 30),
          items: [
            { text: "Look at the two prepayments that end this month", status: "done" },
            {
              text: "Draft the release journals for the two prepayments that end this month, in Xero.",
              status: "active",
            },
            { text: "Check the journals add up to the schedule", status: "pending" },
          ],
        },
      },
    ],
  };

  // One run that says what it will do between its calls, so its work falls in two blocks (the
  // production chat of 27 October). The server sends the request's latest plan on the turn that
  // holds its first, so the card stays over the first block and the second follows it; the two
  // failures it got past (a Word file read_file cannot read, a first try at reading it in code)
  // do not hold the first block open.
  const sopFile = {
    id: "file_sop_001",
    name: "SOP 001 Print and enter purchase orders.docx",
    at: day(71, 14, 2),
    size: 639_000,
    mediaType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  };
  const sopPlan = (done: number, active: boolean) => ({
    at: day(71, 14, 4),
    items: [
      "Read the SOP document",
      "Check the diagram guide",
      "Draw the flowchart for an AI teammate",
      "Check the drawing and hand it over",
    ].map((text, index) => {
      const status =
        index < done
          ? ("done" as const)
          : index === done && active
            ? ("active" as const)
            : ("pending" as const);
      // Each item began a little before the block of work that served it, which names the block.
      return status === "pending"
        ? { text, status }
        : { text, status, startedAt: day(71, 14, [2, 4, 9, 11][index] ?? 11).toISOString() };
    }),
  });
  const sopRun = (id: string, minute: number, purpose: string, patch = {}) => ({
    id,
    at: day(71, 14, minute),
    kind: "run" as const,
    status: "done" as const,
    purpose,
    code: "print('...')",
    printed: "",
    exitCode: 0,
    ms: 700,
    ...patch,
  });
  const sopDiagram: Chat = {
    id: "sop-diagram",
    title: "SOP 001 as an AI teammate",
    startedBy: "me",
    startedAt: day(71, 14, 2),
    lastAt: day(71, 14, 12),
    preview: "Here is the diagram: a top-down flowchart of SOP 001.",
    needsYou: false,
    files: [sopFile],
    steps: [],
    messages: [
      {
        id: "sop-diagram-m1",
        author: "me",
        at: day(71, 14, 2),
        text: "Build a diagram of this SOP as if we are going to employ an AI teammate to automate it.",
        files: [sopFile],
      },
      {
        id: "sop-diagram-m2",
        author: "assistant",
        at: day(71, 14, 3),
        text: "",
        runId: "run_sop_diagram",
        requestRunId: "run_sop_diagram",
        work: [
          {
            id: "sop-read",
            at: day(71, 14, 3),
            kind: "read",
            status: "failed",
            fileId: sopFile.id,
          },
          sopRun("sop-skills", 3, "List the skills guides", { ms: 3_300 }),
        ],
        plan: sopPlan(4, false),
      },
      {
        id: "sop-diagram-m3",
        author: "assistant",
        at: day(71, 14, 5),
        text: "I'll draw a top-down flowchart of the SOP for an AI teammate: the steps it takes on, the one decision (short stock), and where a person still signs off.",
        runId: "run_sop_diagram",
        requestRunId: "run_sop_diagram",
        work: [
          sopRun("sop-text-1", 4, "Read the SOP Word document text", {
            status: "failed",
            exitCode: 1,
            opened: [sopFile.id],
            errors: "ModuleNotFoundError: No module named 'docx2txt'",
          }),
          sopRun("sop-text-2", 4, "Read the SOP Word document text"),
          sopRun("sop-guide", 5, "Read the diagram guide", { ms: 300 }),
        ],
      },
      {
        id: "sop-diagram-m4",
        author: "assistant",
        at: day(71, 14, 11),
        text: "",
        runId: "run_sop_diagram",
        requestRunId: "run_sop_diagram",
        work: [
          sopRun("sop-draw", 10, "Draw the flowchart", {
            ms: 4_100,
            made: [
              {
                id: "file_sop_png",
                name: "sop001-ai-automation.png",
                mediaType: "image/png",
                size: 182_000,
              },
            ],
          }),
        ],
      },
      {
        id: "sop-diagram-m5",
        author: "assistant",
        at: day(71, 14, 12),
        text: "Here is the diagram: a top-down flowchart of SOP 001 redrawn as an AI-teammate playbook. I kept the repeated per-order steps as one box.",
        runId: "run_sop_diagram",
        requestRunId: "run_sop_diagram",
      },
    ],
  };

  // A reply that changed a playbook's step and a row of a table it reads (Paper p-15-0, SV22 b):
  // its card lists the table line after the step line, with an Undo of its own. Its playbook and
  // table live in the Processes and Tables APIs, so the card shows where those answer.
  const shipOrders: Chat = {
    id: "ship-orders",
    title: "Ship orders",
    startedBy: "me",
    startedAt: day(71, 16, 0),
    lastAt: day(71, 16, 1),
    preview: "Redline's cutoff is 3 pm now, and step 02 books in the carrier's portal.",
    needsYou: false,
    files: [],
    steps: [],
    messages: [
      {
        id: "ship-orders-m1",
        author: "me",
        at: day(71, 16, 0),
        text: "Redline moved their pickup cutoff to 3 pm. Book every carrier in their own portal.",
      },
      {
        id: "ship-orders-m2",
        author: "assistant",
        at: day(71, 16, 1),
        text: "Done. Redline's cutoff is 3 pm in the Carriers table, Kestrel Freight and Bluegum Couriers are in it too, and step 02 books each pickup in the carrier's portal.",
        processChanges: [
          {
            processId: "proc_01k8x6m7fqe9v0w2y4z6a8c0f2",
            draftId: "pdraft_01k8x6m7fqe9v0w2y4z6a8c0f2",
            batchId: "pchg_01k8x6m7fqe9v0w2y4z6a8c0f7",
            revision: 2,
            stepIds: ["step_01k8x6m7fqe9v0w2y4z6a8c0f3"],
          },
        ],
        tableChanges: [
          {
            callId: "call_ship_orders_1",
            tableId: "dtbl_01k8x6m7fqe9v0w2y4z6a8c0f2",
            table: "Carriers",
            made: false,
            rows: 3,
            lines: [
              {
                kind: "row_changed",
                text: "Carriers · Redline's cutoff 2 pm → 3 pm",
                rows: [{ rowId: "drow_01k8x6m7fqe9v0w2y4z6a8c0f2", revision: 2 }],
              },
              {
                kind: "rows_added",
                text: "Carriers · added Kestrel Freight and Bluegum Couriers",
                rows: [
                  { rowId: "drow_01k8x6m7fqe9v0w2y4z6a8c0f3", revision: 1 },
                  { rowId: "drow_01k8x6m7fqe9v0w2y4z6a8c0f4", revision: 1 },
                ],
              },
            ],
          },
        ],
      },
    ],
  };

  // A reply that wrote a playbook's first draft (Paper p-15-0, SV21 a): its card shows the
  // playbook, and Open playbook opens its page, changing, with this chat docked (PO2). The
  // playbook lives in the Processes API, so the card shows where that answers.
  const orderCancellations: Chat = {
    id: "order-cancellations",
    title: "Order cancellations",
    startedBy: "me",
    startedAt: day(71, 17, 0),
    lastAt: day(71, 17, 2),
    preview: "Here is a first draft of Cancel an order. It needs to know what one case is.",
    needsYou: false,
    files: [],
    steps: [],
    messages: [
      {
        id: "order-cancellations-m1",
        author: "me",
        at: day(71, 17, 0),
        text: "Make a playbook for cancelling an order when a customer asks by email.",
      },
      {
        id: "order-cancellations-m2",
        author: "assistant",
        at: day(71, 17, 2),
        text: "Here is a first draft of Cancel an order. It picks up the request from the orders inbox; before it can publish, it needs to know what one case is.",
        processChanges: [
          {
            processId: "proc_01k8x6m7fqe9v0w2y4z6a8c0f9",
            draftId: "pdraft_01k8x6m7fqe9v0w2y4z6a8c0f9",
            batchId: "pchg_01k8x6m7fqe9v0w2y4z6a8c0f9",
            revision: 1,
            stepIds: ["step_01k8x6m7fqe9v0w2y4z6a8c0f2"],
          },
        ],
      },
    ],
  };

  // A try started from the chat (Paper p-1G-0, TR7 A): its card reads the try's case from the Cases
  // API and follows it there, with what the try asks of the person, so it shows where that answers.
  const kestrelTry: Chat = {
    id: "kestrel-try",
    title: "Try order entry on the Kestrel PO",
    startedBy: "me",
    startedAt: day(72, 9, 0),
    lastAt: day(72, 9, 1),
    preview: "Trying Order entry on PO 4471 · Kestrel Retail.",
    needsYou: false,
    files: [],
    steps: [],
    messages: [
      {
        id: "kestrel-try-m1",
        author: "me",
        at: day(72, 9, 0),
        text: "Try it on the Kestrel PO from this morning",
      },
      {
        id: "kestrel-try-m2",
        author: "assistant",
        at: day(72, 9, 1),
        text: "Trying Order entry on PO 4471 · Kestrel Retail, the draft as it stands. Nothing it would change or send goes out; what it needs from you shows here.",
        tries: [
          {
            caseId: "case_01k8x6m7fqe9v0w2y4z6a8c0t1",
            processId: "proc_01k8x6m7fqe9v0w2y4z6a8c0t1",
          },
        ],
      },
    ],
  };

  // The same try run again later in the chat (Paper p-1G-0, LT1): its new card is drawn at the turn
  // that ran it, the first folds to a line, and what it asks comes at the end, after the turns since.
  const kestrelTryAgain: Chat = {
    ...kestrelTry,
    id: "kestrel-try-again",
    title: "Run the Kestrel try again",
    lastAt: day(72, 11, 6),
    messages: [
      ...kestrelTry.messages.map((message) => ({
        ...message,
        id: message.id.replace("kestrel-try", "kestrel-try-again"),
      })),
      {
        id: "kestrel-try-again-m3",
        author: "me",
        at: day(72, 11, 0),
        text: "Run the try again from step 02",
      },
      {
        id: "kestrel-try-again-m4",
        author: "assistant",
        at: day(72, 11, 1),
        text: "Running the try on PO 4471 · Kestrel Retail again from step 02.",
        tries: [
          {
            caseId: "case_01k8x6m7fqe9v0w2y4z6a8c0t1",
            processId: "proc_01k8x6m7fqe9v0w2y4z6a8c0t1",
          },
        ],
      },
      {
        id: "kestrel-try-again-m5",
        author: "me",
        at: day(72, 11, 5),
        text: "While it runs: what does step 03 check?",
      },
      {
        id: "kestrel-try-again-m6",
        author: "assistant",
        at: day(72, 11, 6),
        text: "Step 03 checks each line against the price list, and asks you when a price differs.",
      },
    ],
  };

  return [
    ...recent,
    ...earlierThisMonth,
    ...older,
    prepayments,
    sopDiagram,
    shipOrders,
    orderCancellations,
    kestrelTry,
    kestrelTryAgain,
  ];
}

/**
 * Chats their owner put away. The list leaves them out until it is asked to include archived chats,
 * so they are kept apart from the ones the Paper boards count.
 */
export function sampleArchivedChats(now: Date): Chat[] {
  return [
    {
      ...quick(now, {
        id: "old-vat-query",
        title: "VAT on the Lisbon trip",
        daysAgo: 16,
        hour: 11,
        ask: "Can we reclaim the VAT on the Lisbon trip?",
        reply: "Not on the hotel, which was booked without an invoice. The flights qualify.",
      }),
      archivedAt: at(now, 9, 10),
    },
  ];
}

/**
 * The chats the person last opened, newest first. The sidebar's "Recent chats" lists these, and
 * the order is the one the Paper boards draw. Opening a chat that is not here puts it first.
 */
export const SAMPLE_RECENT_IDS: readonly string[] = [
  "invoice-delays",
  "monthly-close",
  "onboarding-questions",
  "xero-coding-rules",
];

/**
 * What Abacor answers when a person replies in a chat that has a scripted answer, by chat id: the
 * text the service will stream in bursts (`client.stream`). It holds the four things the answer
 * widgets draw, an inline process link, a table, a card in a card and a block of code, so a reply
 * in "Accrual questions" shows all of them arriving. Every other chat still gets no answer.
 */
export const SAMPLE_ANSWERS: Readonly<Record<string, string>> = {
  "accrual-questions": lines([
    "Two things are still open for the October close.",
    "",
    "| Item | Owner | Status |",
    "|---|---|---|",
    "| October utilities invoice | :person[Tom] | :tag[Waiting]{tone=warning} |",
    "| Bank reconciliation | Ada | :tag[2 items open]{tone=warning} |",
    "| Rent accrual | Ada | :tag[Posted]{tone=success} |",
    "",
    "The utilities invoice is the one that can move the numbers. When it arrives it goes through :process[Prepare supplier bills]{id=prepare-supplier-bills}, and I check its **bank details** against Xero before anything is paid.",
    "",
    '::::card{title="What I would post today" tag="Draft" tone="neutral"}',
    "Until the invoice is here I would accrue utilities at last month's amount, so the books can close on time.",
    "",
    ':::card{title="If the invoice differs by more than 10%" tag="Asks you" tone="warning"}',
    "I stop and ask :person[Tom] before I post the difference. Nothing is posted until a person approves it.",
    ":::",
    "",
    "```text",
    "Dr  Utilities expense      612.40",
    "  Cr  Accrued expenses             612.40",
    "```",
    "::::",
    "",
    "Nothing has been posted. Say the word and I will draft the entry for you to review.",
  ]),
};
